import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Music, Send, List, Phone, Instagram, Copy, Check, Youtube, Heart, Clock, Link as LinkIcon, Globe, MapPin, Mail, User, Calendar, ChevronDown, ChevronUp, Flame } from 'lucide-react';
import { FaTiktok, FaFacebook, FaInstagram } from 'react-icons/fa';
import LanguageSelector from '@/components/LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ========== i18n - Traductions ==========
const SUPPORTED_LANGS = ['fr', 'en', 'es', 'de', 'pt'];
const DEFAULT_LANG = 'en';

const TEXT = {
  fr: {
    title: "Choisis la musique de la soirée",
    subtitle: "Une envie ? Une dédicace ?",
    name: "Prénom",
    name_placeholder: "Ton prénom",
    song: "Quelle musique veux-tu entendre ?",
    song_placeholder: "Ex : Titanium - David Guetta",
    artist: "Artiste",
    artist_placeholder: "Nom de l'artiste",
    media_url: "Lien Spotify ou YouTube",
    media_url_placeholder: "Colle le lien ici",
    msg: "Message / Dédicace",
    msg_placeholder: "Un petit mot pour le DJ...",
    send: "Envoyer la demande",
    sending: "Envoi...",
    other: "Voir les autres demandes",
    all: "Toutes les demandes",
    back: "Retour",
    make_request: "Faire une demande",
    status_received: "Reçue",
    status_pending: "En attente",
    status_played: "Jouée",
    status_refused: "Refusée",
    reason_already_played: "Musique déjà passée",
    reason_not_in_theme: "Ne correspond pas au thème de la soirée",
    reason_already_requested: "Musique déjà demandée",
    closed_title: "Les demandes sont temporairement fermées",
    closed_text: "Le DJ reprendra les demandes si la vibe de la soirée s'y prête",
    event_requests_closed: "Les demandes sont fermées pour cet événement",
    no_event: "Aucune soirée en cours",
    no_event_text: "Le DJ n'a pas encore démarré de session. Reviens plus tard !",
    event_closed: "Cet événement est terminé",
    event_closed_text: "Les demandes ne sont plus acceptées pour cet événement",
    event_name: "Événement",
    empty: "Aucune demande pour le moment",
    be_first: "Sois le premier à demander un son !",
    error_submit: "Erreur lors de l'envoi",
    don_title: "Soutenir le DJ",
    don_text: "Si tu apprécies la musique, tu peux laisser un pourboire ou un avis",
    don_paypal: "Pourboire via PayPal",
    don_tip_msg: "Un pourboire est toujours apprécié 💛",
    don_review: "Laisser un avis positif",
    dj_label: "DJ de la soirée",
    loading: "Chargement...",
    invalid_url: "URL invalide",
    // Phase 1 - Simplification
    add_details: "+ Ajouter des détails (facultatif)",
    hide_details: "- Masquer les détails",
    optional: "Facultatif",
    // Phase 2 - Engagement likes
    people_support: "personnes soutiennent ce titre",
    person_supports: "personne soutient ce titre",
    top_supported: "🔥 Titres les plus soutenus",
    support_others: "Tu peux soutenir d'autres titres pour influencer l'ambiance 🔥",
    // Phase 6 - Event name
    event_party: "🎉 Soirée",
    event_subtitle: "Faites vibrer cette soirée avec vos titres 🔥",
    // Authority / Music preferences
    authority_title: "🎛️ Choix du donneur d'ordre",
    authority_decision: "Décision :",
    authority_banned_title: "🚫 Interdits",
    authority_loves_title: "❤️ Envies",
    authority_see_more: "Voir plus",
    authority_see_less: "Voir moins",
    banned_warning_title: "⚠️ Attention",
    banned_warning_text: "Cette demande semble correspondre à un interdit du donneur d'ordre",
    banned_warning_modify: "Modifier ma demande",
    banned_warning_send: "Envoyer quand même",
    // Duplicate detection
    already_requested: "Ce son a déjà été proposé 🎧",
    support_instead: "Tu peux le soutenir en cliquant sur ❤️",
    support_btn: "Soutenir",
    similar_found: "Sons similaires déjà demandés",
    duplicate_blocked: "Ce morceau est déjà dans la liste 👍",
    duplicate_played: "Ce morceau a déjà été joué 🎵",
    vote_instead: "Clique sur ❤️ pour voter"
  },
  en: {
    title: "Choose the music for tonight",
    subtitle: "Got a song in mind? A dedication?",
    name: "First name",
    name_placeholder: "Your first name",
    song: "Song title",
    song_placeholder: "Name of the song",
    artist: "Artist",
    artist_placeholder: "Artist name",
    media_url: "Spotify or YouTube link",
    media_url_placeholder: "Paste the link here",
    msg: "Message / Dedication",
    msg_placeholder: "A word for the DJ...",
    send: "Send request",
    sending: "Sending...",
    other: "See other requests",
    all: "All requests",
    back: "Back",
    make_request: "Make a request",
    status_received: "Received",
    status_pending: "Pending",
    status_played: "Played",
    status_refused: "Refused",
    reason_already_played: "Already played during the night",
    reason_not_in_theme: "Does not fit the vibe of the night",
    reason_already_requested: "Already requested by someone else",
    closed_title: "Requests are temporarily closed",
    closed_text: "The DJ will reopen requests if the vibe allows it",
    event_requests_closed: "Requests are closed for this event",
    no_event: "No party in progress",
    no_event_text: "The DJ hasn't started a session yet. Check back later!",
    event_closed: "This event has ended",
    event_closed_text: "Requests are no longer accepted for this event",
    event_name: "Event",
    empty: "No requests yet",
    be_first: "Be the first to make a request!",
    error_submit: "Error submitting request",
    don_title: "Support the DJ",
    don_text: "If you like the music, you can leave a tip or a review",
    don_paypal: "Tip via PayPal",
    don_tip_msg: "A tip is always appreciated 💛",
    don_review: "Leave a positive review",
    dj_label: "DJ of the night",
    loading: "Loading...",
    invalid_url: "Invalid URL",
    // Phase 1 - Simplification
    add_details: "+ Add details (optional)",
    hide_details: "- Hide details",
    optional: "Optional",
    // Phase 2 - Engagement likes
    people_support: "people support this track",
    person_supports: "person supports this track",
    top_supported: "🔥 Most supported tracks",
    support_others: "Support other tracks to influence the vibe 🔥",
    // Phase 6 - Event name
    event_party: "🎉 Party",
    event_subtitle: "Make this party vibrate with your tracks 🔥",
    // Authority / Music preferences
    authority_title: "🎛️ Host's choice",
    authority_decision: "Decision:",
    authority_banned_title: "🚫 Banned",
    authority_loves_title: "❤️ Wishes",
    authority_see_more: "See more",
    authority_see_less: "See less",
    banned_warning_title: "⚠️ Warning",
    banned_warning_text: "This request seems to match a banned item by the host",
    banned_warning_modify: "Modify my request",
    banned_warning_send: "Send anyway",
    // Duplicate detection
    already_requested: "This song has already been requested 🎧",
    support_instead: "You can support it by clicking ❤️",
    support_btn: "Support",
    similar_found: "Similar songs already requested",
    duplicate_blocked: "This track is already in the list 👍",
    duplicate_played: "This track has already been played 🎵",
    vote_instead: "Click ❤️ to vote"
  },
  es: {
    title: "Elige la música de la noche",
    subtitle: "¿Un tema en mente? ¿Una dedicatoria?",
    name: "Nombre",
    name_placeholder: "Tu nombre",
    song: "Título de la canción",
    song_placeholder: "Nombre de la canción",
    artist: "Artista",
    artist_placeholder: "Nombre del artista",
    media_url: "Enlace de Spotify o YouTube",
    media_url_placeholder: "Pega el enlace aquí",
    msg: "Mensaje / Dedicatoria",
    msg_placeholder: "Un mensaje para el DJ...",
    send: "Enviar petición",
    sending: "Enviando...",
    other: "Ver otras peticiones",
    all: "Todas las peticiones",
    back: "Volver",
    make_request: "Hacer una petición",
    status_received: "Recibida",
    status_pending: "Pendiente",
    status_played: "Reproducida",
    status_refused: "Rechazada",
    reason_already_played: "Ya se ha reproducido durante la noche",
    reason_not_in_theme: "No encaja con el ambiente de la noche",
    reason_already_requested: "Ya solicitada por otra persona",
    closed_title: "Las peticiones están temporalmente cerradas",
    closed_text: "El DJ volverá a abrirlas si el ambiente lo permite",
    event_requests_closed: "Las peticiones están cerradas para este evento",
    no_event: "No hay fiesta en curso",
    no_event_text: "El DJ aún no ha iniciado sesión. ¡Vuelve más tarde!",
    event_closed: "Este evento ha terminado",
    event_closed_text: "Ya no se aceptan peticiones para este evento",
    event_name: "Evento",
    empty: "Aún no hay peticiones",
    be_first: "¡Sé el primero en hacer una petición!",
    error_submit: "Error al enviar",
    don_title: "Apoyar al DJ",
    don_text: "Si te gusta la música, puedes dejar una propina o una reseña",
    don_paypal: "Propina via PayPal",
    don_tip_msg: "Una propina siempre es bienvenida 💛",
    don_review: "Dejar una reseña positiva",
    dj_label: "DJ de la noche",
    loading: "Cargando...",
    invalid_url: "URL no válida",
    // Phase 1 - Simplification
    add_details: "+ Añadir detalles (opcional)",
    hide_details: "- Ocultar detalles",
    optional: "Opcional",
    // Phase 2 - Engagement likes
    people_support: "personas apoyan este tema",
    person_supports: "persona apoya este tema",
    top_supported: "🔥 Temas más apoyados",
    support_others: "Apoya otros temas para influir en el ambiente 🔥",
    // Phase 6 - Event name
    event_party: "🎉 Fiesta",
    event_subtitle: "¡Haz vibrar esta fiesta con tus temas 🔥",
    // Duplicate detection
    already_requested: "Este tema ya ha sido solicitado 🎧",
    support_instead: "Puedes apoyarlo haciendo clic en ❤️",
    support_btn: "Apoyar",
    similar_found: "Temas similares ya solicitados",
    duplicate_blocked: "Este tema ya está en la lista 👍",
    duplicate_played: "Este tema ya se ha reproducido 🎵",
    vote_instead: "Haz clic en ❤️ para votar"
  },
  de: {
    title: "Wähle die Musik des Abends",
    subtitle: "Ein Wunsch? Eine Widmung?",
    name: "Vorname",
    name_placeholder: "Dein Vorname",
    song: "Songtitel",
    song_placeholder: "Name des Songs",
    artist: "Künstler",
    artist_placeholder: "Name des Künstlers",
    media_url: "Spotify- oder YouTube-Link",
    media_url_placeholder: "Link hier einfügen",
    msg: "Nachricht / Widmung",
    msg_placeholder: "Ein Wort an den DJ...",
    send: "Anfrage senden",
    sending: "Wird gesendet...",
    other: "Andere Anfragen ansehen",
    all: "Alle Anfragen",
    back: "Zurück",
    make_request: "Anfrage stellen",
    status_received: "Empfangen",
    status_pending: "Ausstehend",
    status_played: "Gespielt",
    status_refused: "Abgelehnt",
    reason_already_played: "Bereits während des Abends gespielt",
    reason_not_in_theme: "Passt nicht zur Stimmung des Abends",
    reason_already_requested: "Bereits von jemand anderem angefragt",
    closed_title: "Anfragen sind vorübergehend geschlossen",
    closed_text: "Der DJ öffnet sie wieder, wenn es zur Stimmung passt",
    event_requests_closed: "Anfragen sind für dieses Event geschlossen",
    no_event: "Keine Party im Gange",
    no_event_text: "Der DJ hat noch keine Session gestartet. Komm später wieder!",
    event_closed: "Dieses Event ist beendet",
    event_closed_text: "Anfragen werden für dieses Event nicht mehr angenommen",
    event_name: "Event",
    empty: "Noch keine Anfragen",
    be_first: "Sei der Erste!",
    error_submit: "Fehler beim Senden",
    don_title: "DJ unterstützen",
    don_text: "Wenn dir die Musik gefällt, kannst du ein Trinkgeld oder eine Bewertung hinterlassen",
    don_paypal: "Trinkgeld via PayPal",
    don_tip_msg: "Ein Trinkgeld ist immer willkommen 💛",
    don_review: "Positive Bewertung hinterlassen",
    dj_label: "DJ des Abends",
    loading: "Lädt...",
    invalid_url: "Ungültige URL",
    // Phase 1 - Simplification
    add_details: "+ Details hinzufügen (optional)",
    hide_details: "- Details ausblenden",
    optional: "Optional",
    // Phase 2 - Engagement likes
    people_support: "Personen unterstützen diesen Track",
    person_supports: "Person unterstützt diesen Track",
    top_supported: "🔥 Meistunterstützte Tracks",
    support_others: "Unterstütze andere Tracks, um die Stimmung zu beeinflussen 🔥",
    // Phase 6 - Event name
    event_party: "🎉 Party",
    event_subtitle: "Lass diese Party mit deinen Tracks vibrieren 🔥",
    // Duplicate detection
    already_requested: "Dieser Song wurde bereits angefragt 🎧",
    support_instead: "Du kannst ihn unterstützen, indem du auf ❤️ klickst",
    support_btn: "Unterstützen",
    similar_found: "Ähnliche Songs bereits angefragt",
    duplicate_blocked: "Dieser Track ist bereits in der Liste 👍",
    duplicate_played: "Dieser Track wurde bereits gespielt 🎵",
    vote_instead: "Klicke auf ❤️ zum Abstimmen"
  },
  pt: {
    title: "Escolha a música da noite",
    subtitle: "Um desejo? Uma dedicatória?",
    name: "Nome",
    name_placeholder: "Seu nome",
    song: "Qual música você quer ouvir?",
    song_placeholder: "Ex: Titanium - David Guetta",
    artist: "Artista",
    artist_placeholder: "Nome do artista",
    media_url: "Link Spotify ou YouTube",
    media_url_placeholder: "Cole o link aqui",
    msg: "Mensagem / Dedicatória",
    msg_placeholder: "Uma palavra para o DJ...",
    send: "Enviar pedido",
    sending: "Enviando...",
    thanks_title: "Valeu! 🎵",
    thanks_text: "Seu pedido foi enviado",
    another: "Fazer outro pedido",
    already_requested: "Este título já foi solicitado",
    reason_blocked: "Você foi bloqueado para fazer pedidos",
    reason_forbidden: "Título contém palavras proibidas",
    reason_already_requested: "Já solicitado por outra pessoa",
    closed_title: "Os pedidos estão temporariamente fechados",
    closed_text: "O DJ vai reabrir se o clima permitir",
    event_requests_closed: "Os pedidos estão fechados para este evento",
    no_event: "Nenhuma festa em andamento",
    no_event_text: "O DJ ainda não iniciou a sessão. Volte mais tarde!",
    event_closed: "Este evento terminou",
    event_closed_text: "Não aceitamos mais pedidos para este evento",
    event_name: "Evento",
    empty: "Nenhum pedido ainda",
    be_first: "Seja o primeiro a fazer um pedido!",
    error_submit: "Erro ao enviar",
    don_title: "Apoiar o DJ",
    don_text: "Se você gosta da música, pode deixar uma gorjeta ou uma avaliação",
    don_paypal: "Gorjeta via PayPal",
    don_tip_msg: "Uma gorjeta é sempre bem-vinda 💛",
    don_review: "Deixar uma avaliação positiva",
    dj_label: "DJ da noite",
    loading: "Carregando...",
    invalid_url: "URL inválida",
    // Phase 1 - Simplification
    add_details: "+ Adicionar detalhes (opcional)",
    hide_details: "- Ocultar detalhes",
    optional: "Opcional",
    // Phase 2 - Engagement likes
    people_support: "pessoas apoiam esta faixa",
    person_supports: "pessoa apoia esta faixa",
    top_supported: "🔥 Faixas mais apoiadas",
    support_others: "Apoie outras faixas para influenciar o clima 🔥",
    // Phase 6 - Event name
    event_party: "🎉 Festa",
    event_subtitle: "Faça esta festa vibrar com suas músicas 🔥",
    // Authority / Music preferences
    authority_title: "🎛️ Escolha do anfitrião",
    authority_decision: "Decisão:",
    authority_banned_title: "🚫 Proibidos",
    authority_loves_title: "❤️ Desejos",
    authority_see_more: "Ver mais",
    authority_see_less: "Ver menos",
    banned_warning_title: "⚠️ Atenção",
    banned_warning_text: "Este pedido parece corresponder a um item proibido pelo anfitrião",
    banned_warning_modify: "Modificar meu pedido",
    banned_warning_send: "Enviar mesmo assim",
    // Duplicate detection
    already_requested: "Esta música já foi solicitada 🎧",
    support_instead: "Você pode apoiá-la clicando em ❤️",
    support_btn: "Apoiar",
    similar_found: "Músicas similares já solicitadas",
    duplicate_blocked: "Esta faixa já está na lista 👍",
    duplicate_played: "Esta faixa já foi tocada 🎵",
    vote_instead: "Clique em ❤️ para votar"
  }
};

function t(lang, key) {
  return TEXT[lang]?.[key] ?? TEXT.en[key] ?? key;
}

function getInitialLang() {
  const url = new URL(window.location.href);
  const l = url.searchParams.get('lang');
  if (l && SUPPORTED_LANGS.includes(l)) return l;
  const stored = localStorage.getItem('trackvibes_lang');
  if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  const b = (navigator.language || 'en').slice(0, 2);
  if (SUPPORTED_LANGS.includes(b)) return b;
  return DEFAULT_LANG;
}

function getClientId() {
  let clientId = localStorage.getItem('dj_client_id');
  if (!clientId) {
    clientId = 'client_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem('dj_client_id', clientId);
  }
  return clientId;
}

// ========== Music Autocomplete Component ==========
function MusicAutocomplete({ value, onChange, onSelect, placeholder, disabled, lang }) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Debounced search function
  const searchMusic = async (query) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Don't search if query is too short
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    abortControllerRef.current = new AbortController();

    try {
      const response = await axios.get(`${API}/music/search`, {
        params: { q: query, limit: 5 },
        signal: abortControllerRef.current.signal
      });
      
      if (response.data && response.data.length > 0) {
        setSuggestions(response.data);
        setShowDropdown(true);
        setSelectedIndex(-1);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    } catch (error) {
      // Ignore aborted requests
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
        console.warn('Music search error:', error);
      }
      // Never block - just hide suggestions on error
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce search (350ms)
    debounceTimerRef.current = setTimeout(() => {
      searchMusic(newValue);
    }, 350);
  };

  // Handle suggestion selection
  const handleSelect = (track) => {
    onSelect({
      song_title: track.title,
      artist: track.artist,
      spotify_id: track.spotify_id,
      spotify_url: track.spotify_url
    });
    setShowDropdown(false);
    setSuggestions([]);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          e.preventDefault();
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 rounded-lg px-4 py-3 focus:border-neon-cyan focus:outline-none"
        disabled={disabled}
        required
        data-testid="song-title-input"
        autoComplete="off"
      />
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin"></div>
        </div>
      )}

      {/* Suggestions dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-[#1a1a1a] border border-white/20 rounded-lg shadow-xl overflow-hidden"
        >
          {suggestions.map((track, index) => (
            <button
              key={track.spotify_id || index}
              type="button"
              onClick={() => handleSelect(track)}
              className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                index === selectedIndex 
                  ? 'bg-neon-cyan/20' 
                  : 'hover:bg-white/10'
              }`}
            >
              {/* Album thumbnail */}
              {track.image ? (
                <img 
                  src={track.image} 
                  alt="" 
                  className="w-10 h-10 rounded object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Music className="w-5 h-5 text-gray-500" />
                </div>
              )}
              
              {/* Track info */}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate text-sm">
                  {track.title}
                </p>
                <p className="text-gray-400 text-xs truncate">
                  {track.artist}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ========== Authority Preferences Card Component ==========
function AuthorityPreferencesCard({ currentEvent, lang, t }) {
  const [showAllBanned, setShowAllBanned] = useState(false);
  const [showAllLoves, setShowAllLoves] = useState(false);
  
  const bannedItems = currentEvent.authority_banned || [];
  const lovesItems = currentEvent.authority_loves || [];
  const authorityLabel = currentEvent.authority_label;
  
  const maxVisible = 3;
  
  return (
    <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4 mb-6" data-testid="authority-preferences-card">
      <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
        🎛️ {t(lang, 'authority_title')}
      </h3>
      
      {authorityLabel && (
        <p className="text-gray-300 text-sm mb-3">
          {t(lang, 'authority_decision')} <span className="text-neon-pink font-semibold">{authorityLabel}</span>
        </p>
      )}
      
      <div className="space-y-3">
        {/* Banned items */}
        {bannedItems.length > 0 && (
          <div>
            <p className="text-red-400 text-sm font-medium mb-1">{t(lang, 'authority_banned_title')}</p>
            <div className="space-y-1">
              {(showAllBanned ? bannedItems : bannedItems.slice(0, maxVisible)).map((item, idx) => (
                <p key={idx} className="text-gray-300 text-sm flex items-center gap-2">
                  <span className="text-red-400">❌</span> {item}
                </p>
              ))}
              {bannedItems.length > maxVisible && (
                <button 
                  onClick={() => setShowAllBanned(!showAllBanned)}
                  className="text-neon-cyan text-xs hover:underline mt-1"
                >
                  {showAllBanned ? t(lang, 'authority_see_less') : `${t(lang, 'authority_see_more')} (${bannedItems.length - maxVisible})`}
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Loved items */}
        {lovesItems.length > 0 && (
          <div>
            <p className="text-pink-400 text-sm font-medium mb-1">{t(lang, 'authority_loves_title')}</p>
            <div className="space-y-1">
              {(showAllLoves ? lovesItems : lovesItems.slice(0, maxVisible)).map((item, idx) => (
                <p key={idx} className="text-gray-300 text-sm flex items-center gap-2">
                  <span className="text-yellow-400">⭐</span> {item}
                </p>
              ))}
              {lovesItems.length > maxVisible && (
                <button 
                  onClick={() => setShowAllLoves(!showAllLoves)}
                  className="text-neon-cyan text-xs hover:underline mt-1"
                >
                  {showAllLoves ? t(lang, 'authority_see_less') : `${t(lang, 'authority_see_more')} (${lovesItems.length - maxVisible})`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ========== MAIN COMPONENT ==========
export default function ClientRequestPage() {
  const { djId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const listRef = useRef(null);

  // ========== URL PARAMS (source of truth) ==========
  const modeParam = searchParams.get('mode') || 'pro';
  const langParam = searchParams.get('lang');
  const view = searchParams.get('view') || 'form'; // form or list
  const eventSlugParam = searchParams.get('event'); // Optional: specific event QR code

  // ========== STATE ==========
  const [lang, setLangState] = useState(() => langParam || getInitialLang());
  const [djInfo, setDjInfo] = useState(null);
  const [requests, setRequests] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null); // Track active event
  const [currentEvent, setCurrentEvent] = useState(null); // Track specific event from URL
  const [eventClosed, setEventClosed] = useState(false); // Track if event is closed
  const [eventRequestsOpen, setEventRequestsOpen] = useState(true); // Event-specific is_open flag
  const [noActiveEventMessage, setNoActiveEventMessage] = useState(''); // Message when no event
  const [djIsPro, setDjIsPro] = useState(false); // Track if DJ is PRO
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [forcedFreeMode, setForcedFreeMode] = useState(false); // Track if we forced free mode
  const [showBannedWarning, setShowBannedWarning] = useState(false); // Soft warning for banned words
  const [matchedBannedWord, setMatchedBannedWord] = useState(''); // The banned word that was matched
  
  // Duplicate detection - store only the IDs, derive actual data from requests
  const [similarRequestIds, setSimilarRequestIds] = useState([]); // IDs of similar requests
  const [exactDuplicateId, setExactDuplicateId] = useState(null); // ID of exact duplicate

  const [formData, setFormData] = useState({
    client_name: localStorage.getItem('dj_client_name') || '',
    song_title: '',
    artist: '',
    media_url: '',
    message: '',
    event_slug: eventSlugParam || '', // Pass event slug with request
    // Spotify autocomplete data (optional)
    spotify_id: null,
    spotify_url: null
  });
  
  // Phase 1: Toggle for optional details
  const [showDetails, setShowDetails] = useState(false);
  
  // ========== DUPLICATE DETECTION ==========
  // Normalize text for comparison (lowercase, no accents, trim)
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^\w\s]/g, '') // Remove special chars
      .trim();
  };
  
  // Calculate similarity score (simple)
  const getSimilarity = (str1, str2) => {
    const s1 = normalizeText(str1);
    const s2 = normalizeText(str2);
    if (s1 === s2) return 1.0; // Exact match
    if (!s1 || !s2) return 0;
    // Check if one contains the other
    if (s1.includes(s2) || s2.includes(s1)) return 0.8;
    // Simple word overlap
    const words1 = s1.split(/\s+/);
    const words2 = s2.split(/\s+/);
    const common = words1.filter(w => words2.includes(w));
    return common.length / Math.max(words1.length, words2.length);
  };
  
  // Derive actual duplicate/similar objects from requests state (reactive to likes updates)
  const exactDuplicate = useMemo(() => {
    if (!exactDuplicateId) return null;
    return requests.find(r => r.id === exactDuplicateId) || null;
  }, [exactDuplicateId, requests]);
  
  // Check if the exact duplicate is a played track (blocks submission entirely)
  const isPlayedDuplicate = useMemo(() => {
    return exactDuplicate?.status === 'played';
  }, [exactDuplicate]);
  
  const similarRequests = useMemo(() => {
    if (similarRequestIds.length === 0) return [];
    return similarRequestIds
      .map(id => requests.find(r => r.id === id))
      .filter(Boolean);
  }, [similarRequestIds, requests]);
  
  // Find similar requests in existing list
  // IMPORTANT: Search ALL requests including "played" to detect duplicates properly
  // The DJ dashboard hides played requests, but duplicate detection must include them
  const findSimilarRequests = (songTitle, artist) => {
    if (!songTitle || songTitle.length < 3) {
      setSimilarRequestIds([]);
      setExactDuplicateId(null);
      return;
    }
    
    const normalizedTitle = normalizeText(songTitle);
    const normalizedArtist = normalizeText(artist);
    
    // FIXED: Include ALL requests for duplicate detection (including played)
    // Only exclude "refused" as those were explicitly rejected
    const searchableRequests = requests.filter(r => r.status !== 'refused');
    
    const matches = [];
    let exactMatchId = null;
    let exactMatchStatus = null;
    
    for (const req of searchableRequests) {
      const reqTitle = normalizeText(req.song_title);
      const reqArtist = normalizeText(req.artist);
      
      const titleSimilarity = getSimilarity(songTitle, req.song_title);
      const artistMatch = !artist || !req.artist || 
        normalizedArtist === reqArtist ||
        reqArtist.includes(normalizedArtist) ||
        normalizedArtist.includes(reqArtist);
      
      // Exact duplicate: same title (normalized) + compatible artist
      if (titleSimilarity === 1.0 && artistMatch) {
        exactMatchId = req.id;
        exactMatchStatus = req.status;
      }
      // Similar: high title similarity (only show non-played as suggestions)
      else if (titleSimilarity >= 0.7 && artistMatch && req.status !== 'played') {
        matches.push({ id: req.id, similarity: titleSimilarity, likes: req.likes });
      }
    }
    
    // Sort by similarity then by likes
    matches.sort((a, b) => b.similarity - a.similarity || b.likes - a.likes);
    
    setSimilarRequestIds(matches.slice(0, 3).map(m => m.id)); // Max 3 similar
    setExactDuplicateId(exactMatchId);
  };
  
  // Handle song selection from autocomplete
  const handleSongSelect = (trackData) => {
    setFormData(prev => ({
      ...prev,
      song_title: trackData.song_title,
      artist: trackData.artist || prev.artist,
      spotify_id: trackData.spotify_id,
      spotify_url: trackData.spotify_url
    }));
    // Check for duplicates when selecting from Spotify
    findSimilarRequests(trackData.song_title, trackData.artist);
  };

  // ========== COMPUTED VALUES ==========
  // FALLBACK: If DJ is FREE, force mode=free even if URL says mode=pro
  // EXCEPTION: If eventSlugParam is present, we're on an event form - always treat as PRO context
  const mode = useMemo(() => {
    // Event QR codes are always in PRO context
    if (eventSlugParam) return 'pro';
    
    if (!djInfo) return modeParam; // Wait for DJ info
    const djIsPro = djInfo.is_pro === true;
    if (modeParam === 'pro' && !djIsPro) {
      return 'free'; // Force FREE mode for FREE DJs
    }
    return modeParam;
  }, [modeParam, djInfo, eventSlugParam]);
  
  const showProExtras = useMemo(() => mode === 'pro' && djInfo?.is_pro === true, [mode, djInfo]);
  
  // STRICT SEPARATION: 
  // - If on Event QR (eventSlugParam present): check event.is_open and event.status only
  // - If on General QR (no eventSlugParam): check dj.requests_open only
  const isFormOpen = useMemo(() => {
    if (eventSlugParam) {
      // EVENT QR: Only check event's own state (NEVER check dj.requests_open)
      if (eventClosed) return false; // Event is closed
      return eventRequestsOpen; // Event's own is_open toggle
    } else {
      // GENERAL QR (Free/Pro): Only check DJ's global toggle
      return djInfo?.requests_open !== false;
    }
  }, [eventSlugParam, eventClosed, eventRequestsOpen, djInfo?.requests_open]);

  // ========== URL VALIDATION ==========
  const isValidUrl = (url) => {
    if (!url || !url.trim()) return true; // Empty is valid (optional field)
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const getMediaType = (url) => {
    if (!url) return null;
    const lower = url.toLowerCase();
    if (lower.includes('spotify.com')) return 'spotify';
    if (lower.includes('youtube.com') || lower.includes('youtu.be')) return 'youtube';
    return 'link';
  };

  // ========== URL UPDATE HELPER ==========
  const updateUrl = (newParams) => {
    const params = new URLSearchParams(searchParams);
    // Always preserve the event parameter if it exists
    if (eventSlugParam && !newParams.hasOwnProperty('event')) {
      params.set('event', eventSlugParam);
    }
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, value);
      }
    });
    setSearchParams(params, { replace: true });
  };

  // ========== LANGUAGE CHANGE ==========
  const handleLangChange = (newLang) => {
    setLangState(newLang);
    localStorage.setItem('trackvibes_lang', newLang);
    updateUrl({ lang: newLang });
  };

  // ========== NAVIGATION ==========
  const goToForm = () => updateUrl({ view: 'form' });
  const goToList = () => updateUrl({ view: 'list' });
  const scrollToList = () => {
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // ========== FETCH DJ INFO ==========
  useEffect(() => {
    const fetchDJ = async () => {
      try {
        // IMPORTANT: If we have an eventSlugParam, skip redirect check
        // Event URLs should never redirect to DJ PRO form
        if (!eventSlugParam) {
          // Only check redirect for non-event URLs
          try {
            const redirectCheck = await axios.get(`${API}/dj/${djId}/redirect`);
            if (redirectCheck.data.redirect && redirectCheck.data.new_slug) {
              // Redirect to the clean slug, preserving query params
              const currentParams = new URLSearchParams(searchParams);
              const newUrl = `/request/${redirectCheck.data.new_slug}?${currentParams.toString()}`;
              window.location.replace(newUrl);
              return;
            }
          } catch (redirectErr) {
            // Redirect endpoint might not exist, continue normally
            console.log('No redirect needed');
          }
        }
        
        const response = await axios.get(`${API}/dj/${djId}/public`);
        setDjInfo(response.data);
        
        // Check if we need to force FREE mode (DJ is FREE but URL has mode=pro)
        // Only apply this logic for non-event URLs
        const djIsPro = response.data.is_pro === true;
        if (!eventSlugParam && modeParam === 'pro' && !djIsPro) {
          setForcedFreeMode(true);
        }
      } catch (err) {
        console.error('Error fetching DJ:', err);
        setError('DJ not found');
      } finally {
        setLoading(false);
      }
    };
    if (djId) fetchDJ();
  }, [djId, eventSlugParam]); // Only depend on djId and eventSlugParam, NOT searchParams

  // ========== FETCH REQUESTS (INDEPENDENT OF LANG) ==========
  useEffect(() => {
    let alive = true;
    let timeoutId = null;
    // Adaptive polling intervals (ms):
    //   fast   = 12s (default, active event with recent changes)
    //   medium = 25s (no change for 60s+)
    //   slow   = 45s (no change for 3min+, quiet event)
    const INTERVAL_FAST = 12000;
    const INTERVAL_MEDIUM = 25000;
    const INTERVAL_SLOW = 45000;
    let currentInterval = INTERVAL_FAST;
    let lastSignature = null;
    let unchangedCount = 0;
    
    const fetchRequests = async () => {
      try {
        // NOTE: NO lang parameter - language only affects UI
        // PRIORITY: If eventSlugParam is present, ALWAYS use event-specific endpoint
        // This ensures we NEVER fall back to DJ PRO form when on an event URL
        const url = eventSlugParam 
          ? `${API}/requests/${djId}/public?event=${eventSlugParam}`
          : `${API}/requests/${djId}/public`;
        
        const response = await axios.get(url);
        if (alive) {
          setRequests(response.data.requests || []);
          setActiveEvent(response.data.active_event || null);
          
          // STRICT PRIORITY: Event URL takes precedence
          if (eventSlugParam) {
            // QR Event: ALWAYS use the specific event from response, never fallback
            const eventData = response.data.event;
            setCurrentEvent(eventData || null);
            // Debug log to track event loading
            console.log('[TrackVibes] Event form loaded:', eventSlugParam, eventData?.name);
          } else {
            // QR PRO: use active_event as currentEvent (if exists)
            setCurrentEvent(response.data.active_event || response.data.event || null);
          }
          
          setEventClosed(response.data.event_closed || false);
          // STRICT SEPARATION: Get event's own is_open state
          setEventRequestsOpen(response.data.event_requests_open !== false);
          setNoActiveEventMessage(response.data.message || '');
          setDjIsPro(response.data.is_pro || false);

          // Adaptive backoff: slow down polling if content hasn't changed
          const reqs = response.data.requests || [];
          const signature = `${reqs.length}|${reqs.map(r => r.id + (r.likes || 0) + (r.status || '')).join(',')}`;
          if (signature === lastSignature) {
            unchangedCount += 1;
          } else {
            unchangedCount = 0;
            lastSignature = signature;
          }
          // Tier the interval
          if (unchangedCount >= 6) currentInterval = INTERVAL_SLOW;       // ~3min stable
          else if (unchangedCount >= 3) currentInterval = INTERVAL_MEDIUM; // ~1min stable
          else currentInterval = INTERVAL_FAST;
        }
      } catch (err) {
        // On 429 (rate limited) back off aggressively
        if (err?.response?.status === 429) {
          const retryAfter = parseInt(err.response.headers?.['retry-after'] || '30', 10);
          currentInterval = Math.max(currentInterval, (retryAfter + 5) * 1000);
        }
        console.error('Error fetching requests:', err);
      }
    };

    const scheduleNext = () => {
      if (!alive) return;
      timeoutId = setTimeout(async () => {
        if (document.hidden) return; // will resume on visibilitychange
        await fetchRequests();
        scheduleNext();
      }, currentInterval);
    };

    const startPolling = () => {
      if (timeoutId || !alive) return;
      scheduleNext();
    };
    
    const stopPolling = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        // Reset to fast polling when user returns
        unchangedCount = 0;
        currentInterval = INTERVAL_FAST;
        fetchRequests(); // Immediate refresh when tab becomes active
        startPolling();
      }
    };

    if (djId) {
      fetchRequests();
      startPolling();
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        alive = false;
        stopPolling();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [djId, eventSlugParam]); // Depends on djId and eventSlugParam

  // ========== FORM HANDLERS ==========
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Trigger duplicate detection when song_title or artist changes
    if (name === 'song_title') {
      findSimilarRequests(value, formData.artist);
    } else if (name === 'artist') {
      findSimilarRequests(formData.song_title, value);
    }
  };

  // ========== CHECK BANNED WORDS ==========
  const checkBannedWords = (songTitle) => {
    if (!currentEvent?.authority_banned || currentEvent.authority_banned.length === 0) {
      return null;
    }
    const titleLower = songTitle.toLowerCase();
    for (const banned of currentEvent.authority_banned) {
      if (titleLower.includes(banned.toLowerCase())) {
        return banned;
      }
    }
    return null;
  };

  // ========== SINGLE SUBMIT HANDLER (same for free AND pro) ==========
  const handleSubmit = async (e, forceSubmit = false) => {
    e.preventDefault();
    setError('');

    // Check if event is closed
    if (eventClosed) {
      setError(t(lang, 'event_closed') || 'Cet événement est terminé');
      return;
    }

    if (!formData.client_name.trim() || !formData.song_title.trim()) {
      setError('Please fill required fields');
      return;
    }

    // Validate media_url if provided
    if (formData.media_url && !isValidUrl(formData.media_url)) {
      setError(t(lang, 'invalid_url'));
      return;
    }

    // STRICT SEPARATION: isFormOpen already handles both cases
    // - Event QR: checks event.is_open only
    // - General QR: checks dj.requests_open only
    if (!isFormOpen) {
      setError(eventSlugParam ? t(lang, 'event_requests_closed') : t(lang, 'closed_title'));
      return;
    }

    // Block exact duplicates - redirect to like or show played message
    if (exactDuplicate) {
      // Different message if the track was already played
      if (exactDuplicate.status === 'played') {
        setError(t(lang, 'duplicate_played'));
      } else {
        setError(`${t(lang, 'duplicate_blocked')} ${t(lang, 'vote_instead')}`);
      }
      return;
    }

    // Check for banned words (soft warning) - only if not forcing submit
    if (!forceSubmit) {
      const matchedWord = checkBannedWords(formData.song_title.trim());
      if (matchedWord) {
        setMatchedBannedWord(matchedWord);
        setShowBannedWarning(true);
        return;
      }
    }

    setSubmitting(true);
    setShowBannedWarning(false);
    try {
      // Save name for next time
      localStorage.setItem('dj_client_name', formData.client_name.trim());

      // Submit request - SAME for free and pro mode
      // Include event_slug if present
      await axios.post(`${API}/requests/${djId}`, {
        client_name: formData.client_name.trim(),
        song_title: formData.song_title.trim(),
        artist: formData.artist.trim(),
        media_url: formData.media_url.trim() || null,
        message: formData.message.trim(),
        client_id: getClientId(),
        event_slug: eventSlugParam || null,
        // Spotify data (from autocomplete selection)
        spotify_id: formData.spotify_id || null,
        spotify_url: formData.spotify_url || null
      });

      // Reset form
      setFormData(prev => ({
        ...prev,
        song_title: '',
        artist: '',
        media_url: '',
        message: '',
        spotify_id: null,
        spotify_url: null
      }));

      // Redirect to success page with all params (preserve event if present)
      const successParams = new URLSearchParams({
        dj: djInfo?.dj_name || 'DJ',
        id: djId,
        mode: mode,
        lang: lang
      });
      if (eventSlugParam) {
        successParams.set('event', eventSlugParam);
      }
      window.location.href = `/success?${successParams.toString()}`;

    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.detail || t(lang, 'error_submit'));
      setSubmitting(false);
    }
  };

  // ========== LIKE HANDLER ==========
  const handleLike = async (requestId) => {
    try {
      const response = await axios.post(`${API}/requests/${requestId}/like`, {
        client_id: getClientId()
      });
      setRequests(prev => prev.map(req => 
        req.id === requestId 
          ? { ...req, likes: response.data.likes, liked_by: response.data.liked_by }
          : req
      ));
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  // ========== STATUS LABEL ==========
  const getStatusLabel = (status) => {
    switch (status) {
      case 'received': return { text: t(lang, 'status_received'), color: 'bg-blue-500' };
      case 'pending': return { text: t(lang, 'status_pending'), color: 'bg-yellow-500' };
      case 'played': return { text: t(lang, 'status_played'), color: 'bg-green-500' };
      case 'refused': return { text: t(lang, 'status_refused'), color: 'bg-red-500' };
      default: return { text: status, color: 'bg-gray-500' };
    }
  };

  // ========== REFUSED REASON TRANSLATION ==========
  const getRefusedReasonText = (reason) => {
    if (!reason) return null;
    switch (reason) {
      case 'already_played': return t(lang, 'reason_already_played');
      case 'not_in_theme': return t(lang, 'reason_not_in_theme');
      case 'already_requested': return t(lang, 'reason_already_requested');
      default: return null;
    }
  };

  // ========== LOADING STATE ==========
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-neon-cyan animate-pulse font-heading text-2xl">{t(lang, 'loading')}</div>
      </div>
    );
  }

  // ========== ERROR STATE ==========
  if (error && !djInfo) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-white mb-4">{error}</h1>
        </div>
      </div>
    );
  }

  // ========== RENDER ==========
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* HEADER */}
      <header className="bg-[#0a0a0a] border-b border-white/10 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          {view === 'list' ? (
            <button onClick={goToForm} className="text-gray-400 hover:text-white text-sm">
              ← {t(lang, 'back')}
            </button>
          ) : (
            <div className="w-16" />
          )}
          <h1 className="font-heading text-lg">{djInfo?.display_name || djInfo?.dj_name || 'DJ'}</h1>
          <LanguageSelector currentLang={lang} onChangeLang={handleLangChange} />
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4">
        
        {/* Event Closed Message */}
        {eventClosed && currentEvent && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-6 text-center">
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Music className="w-6 h-6 text-red-400" />
            </div>
            <h3 className="font-heading text-xl text-white mb-2">{t(lang, 'event_closed')}</h3>
            <p className="text-gray-400 text-sm mb-2">{currentEvent.name}</p>
            <p className="text-gray-500 text-sm">{t(lang, 'event_closed_text')}</p>
          </div>
        )}

        {/* Current Event Banner - Enhanced display */}
        {currentEvent && !eventClosed && (currentEvent.status === 'draft' || currentEvent.status === 'active') && (
          <div className="bg-gradient-to-r from-neon-pink/20 to-neon-cyan/20 border border-neon-pink/30 rounded-xl p-5 mb-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <p className="text-2xl">🎉</p>
              {currentEvent.status === 'active' && (
                <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                  LIVE
                </span>
              )}
            </div>
            <p className="text-white font-heading text-xl">Soirée : "{currentEvent.name}"</p>
            <p className="text-gray-400 text-sm mt-2">Faites vibrer cette soirée avec vos titres 🔥</p>
          </div>
        )}

        {/* Authority / Music Preferences Display */}
        {currentEvent && !eventClosed && (
          (currentEvent.authority_banned?.length > 0 || currentEvent.authority_loves?.length > 0) && (
            <AuthorityPreferencesCard 
              currentEvent={currentEvent} 
              lang={lang} 
              t={t}
            />
          )
        )}
        
        {/* ========== VIEW: FORM ========== */}
        {view !== 'list' && !eventClosed && (
          <>
            {/* Pro Mode: DJ Business Card */}
            {showProExtras && (
              <div className="bg-gradient-to-br from-white/10 to-white/5 border border-neon-cyan/30 rounded-xl p-5 mb-6">
                <div className="flex items-start gap-4">
                  {/* Photo + Logo Column - Stacked vertically */}
                  <div className="flex-shrink-0 flex flex-col gap-3">
                    {/* Profile Photo */}
                    {djInfo?.photo_url && (
                      <img 
                        src={djInfo.photo_url} 
                        alt={djInfo?.display_name || djInfo?.dj_name}
                        className="w-20 h-20 rounded-xl object-cover border-2 border-neon-cyan/50"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    
                    {/* Logo - Below photo, same size */}
                    {djInfo?.logo_url && (
                      <img 
                        src={djInfo.logo_url} 
                        alt={`${djInfo?.display_name || djInfo?.dj_name} logo`}
                        className="w-20 h-20 rounded-xl object-contain border-2 border-white/20 bg-black/30"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    
                    {/* Fallback: Initials avatar if neither photo nor logo */}
                    {!djInfo?.photo_url && !djInfo?.logo_url && (
                      <div className="w-20 h-20 rounded-xl border-2 border-neon-cyan/50 bg-gradient-to-br from-neon-cyan/30 to-neon-pink/30 flex items-center justify-center">
                        <span className="text-2xl font-bold text-white uppercase">
                          {(djInfo?.display_name || djInfo?.dj_name || 'DJ').slice(0, 2)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* DJ Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-xl text-neon-cyan mb-1">
                      {djInfo?.display_name || djInfo?.dj_name}
                    </h3>
                    {djInfo?.full_name && (
                      <p className="text-gray-400 text-sm mb-2">{djInfo.full_name}</p>
                    )}
                    {djInfo?.bio && (
                      <p className="text-gray-500 text-xs italic mb-3">"{djInfo.bio}"</p>
                    )}
                    
                    {/* Contact Info */}
                    <div className="flex flex-wrap gap-2 text-sm">
                      {djInfo?.phone && (
                        <a href={`tel:${djInfo.phone}`} className="flex items-center gap-1 text-gray-400 hover:text-white">
                          <Phone className="w-3.5 h-3.5" />
                          <span>{djInfo.phone}</span>
                        </a>
                      )}
                      {djInfo?.email_public && (
                        <a href={`mailto:${djInfo.email_public}`} className="flex items-center gap-1 text-gray-400 hover:text-white">
                          <Mail className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[150px]">{djInfo.email_public}</span>
                        </a>
                      )}
                      {djInfo?.city && (
                        <span className="flex items-center gap-1 text-gray-500">
                          <MapPin className="w-3.5 h-3.5" />
                          <span>{djInfo.city}</span>
                        </span>
                      )}
                    </div>
                    
                    {/* Social Links - Official brand icons */}
                    <div className="flex items-center gap-3 mt-3">
                      {djInfo?.instagram && (
                        <a 
                          href={`https://instagram.com/${djInfo.instagram}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-pink-500 hover:text-pink-400 transition-colors"
                          aria-label="Instagram"
                        >
                          <FaInstagram className="w-5 h-5" />
                        </a>
                      )}
                      {djInfo?.tiktok && (
                        <a 
                          href={`https://tiktok.com/@${djInfo.tiktok}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-white hover:text-gray-300 transition-colors"
                          aria-label="TikTok"
                        >
                          <FaTiktok className="w-5 h-5" />
                        </a>
                      )}
                      {djInfo?.facebook && (
                        <a 
                          href={`https://facebook.com/${djInfo.facebook}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-400 transition-colors"
                          aria-label="Facebook"
                        >
                          <FaFacebook className="w-5 h-5" />
                        </a>
                      )}
                      {djInfo?.website && (
                        <a 
                          href={djInfo.website.startsWith('http') ? djInfo.website : `https://${djInfo.website}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-neon-cyan hover:text-cyan-300 transition-colors"
                          aria-label="Website"
                        >
                          <Globe className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Requests Closed Warning - Different message for Event vs General */}
            {!isFormOpen && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 text-center">
                <Clock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-amber-300 font-semibold">
                  ⏳ {eventSlugParam ? t(lang, 'event_requests_closed') : t(lang, 'closed_title')}
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {eventSlugParam ? t(lang, 'event_closed_text') : t(lang, 'closed_text')}
                </p>
              </div>
            )}

            {/* FORM - SAME FOR FREE AND PRO */}
            <form onSubmit={handleSubmit} className="space-y-4 mb-8">
              {/* Error */}
              {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-center">
                  {error}
                </div>
              )}

              {/* Name - REQUIRED */}
              <div>
                <label className="text-gray-300 text-sm block mb-1">{t(lang, 'name')} *</label>
                <input
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleChange}
                  placeholder={t(lang, 'name_placeholder')}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 rounded-lg px-4 py-3 focus:border-neon-cyan focus:outline-none"
                  disabled={!isFormOpen}
                  required
                  data-testid="client-name-input"
                />
              </div>

              {/* Song - REQUIRED with Autocomplete */}
              <div>
                <label className="text-gray-300 text-sm block mb-1">{t(lang, 'song')} *</label>
                <MusicAutocomplete
                  value={formData.song_title}
                  onChange={(value) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      song_title: value,
                      // Clear spotify data when manually typing
                      spotify_id: null,
                      spotify_url: null
                    }));
                    // Check for duplicates as user types
                    findSimilarRequests(value, formData.artist);
                  }}
                  onSelect={handleSongSelect}
                  placeholder={t(lang, 'song_placeholder')}
                  disabled={!isFormOpen}
                  lang={lang}
                />
              </div>

              {/* Duplicate Detection Alert */}
              {(exactDuplicate || similarRequests.length > 0) && (
                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/40 rounded-xl p-4" data-testid="duplicate-alert">
                  {/* Exact Duplicate */}
                  {exactDuplicate && (
                    <div className="mb-3">
                      {exactDuplicate.status === 'played' ? (
                        // Already played - different UI
                        <>
                          <p className="text-green-300 font-semibold flex items-center gap-2 mb-2">
                            ✓ {t(lang, 'duplicate_played')}
                          </p>
                          <div className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">{exactDuplicate.song_title}</p>
                              {exactDuplicate.artist && (
                                <p className="text-gray-400 text-sm truncate">{exactDuplicate.artist}</p>
                              )}
                            </div>
                            <span className="text-green-400 text-sm font-medium px-3 py-1 bg-green-500/20 rounded-full">
                              {t(lang, 'status_played')}
                            </span>
                          </div>
                        </>
                      ) : (
                        // Not played - show support option
                        <>
                          <p className="text-purple-300 font-semibold flex items-center gap-2 mb-2">
                            🎧 {t(lang, 'already_requested')}
                          </p>
                          <p className="text-gray-400 text-sm mb-3">{t(lang, 'support_instead')}</p>
                          <div className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">{exactDuplicate.song_title}</p>
                              {exactDuplicate.artist && (
                                <p className="text-gray-400 text-sm truncate">{exactDuplicate.artist}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleLike(exactDuplicate.id)}
                              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200 active:scale-110 ${
                                exactDuplicate.liked_by?.includes(getClientId())
                                  ? 'bg-pink-500 text-white scale-105'
                                  : 'bg-pink-500/20 text-pink-400 hover:bg-pink-500/30 hover:scale-105'
                              }`}
                              data-testid="support-duplicate-btn"
                            >
                              <Heart className={`w-4 h-4 transition-transform duration-200 ${exactDuplicate.liked_by?.includes(getClientId()) ? 'fill-current animate-pulse' : ''}`} />
                              <span className="font-semibold">{exactDuplicate.likes || 0}</span>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Similar Requests */}
                  {similarRequests.length > 0 && !exactDuplicate && (
                    <div>
                      <p className="text-purple-300 font-medium text-sm mb-2 flex items-center gap-2">
                        🔍 {t(lang, 'similar_found')}
                      </p>
                      <div className="space-y-2">
                        {similarRequests.map((req) => (
                          <div key={req.id} className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">{req.song_title}</p>
                              {req.artist && (
                                <p className="text-gray-400 text-xs truncate">{req.artist}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleLike(req.id)}
                              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all duration-200 active:scale-110 ${
                                req.liked_by?.includes(getClientId())
                                  ? 'bg-pink-500 text-white scale-105'
                                  : 'bg-pink-500/20 text-pink-400 hover:bg-pink-500/30 hover:scale-105'
                              }`}
                              data-testid={`support-similar-btn-${req.id}`}
                            >
                              <Heart className={`w-3 h-3 transition-transform duration-200 ${req.liked_by?.includes(getClientId()) ? 'fill-current animate-pulse' : ''}`} />
                              <span className="font-semibold">{req.likes || 0}</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Toggle Details Button */}
              <button
                type="button"
                onClick={() => setShowDetails(!showDetails)}
                className="w-full flex items-center justify-center gap-2 text-neon-cyan text-sm py-2 hover:text-neon-pink transition-colors"
              >
                {showDetails ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    {t(lang, 'hide_details')}
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    {t(lang, 'add_details')}
                  </>
                )}
              </button>

              {/* Optional Fields - Collapsible */}
              {showDetails && (
                <div className="space-y-4 animate-in slide-in-from-top-2 duration-200">
                  {/* Artist */}
                  <div>
                    <label className="text-gray-300 text-sm block mb-1">
                      {t(lang, 'artist')}
                      <span className="text-gray-500 text-xs ml-2">({t(lang, 'optional')})</span>
                    </label>
                    <input
                      name="artist"
                      value={formData.artist}
                      onChange={handleChange}
                      placeholder={t(lang, 'artist_placeholder')}
                      className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 rounded-lg px-4 py-3 focus:border-neon-cyan focus:outline-none"
                      disabled={!isFormOpen}
                      data-testid="artist-input"
                    />
                  </div>

                  {/* Media URL (Spotify or YouTube) */}
                  <div>
                    <label className="text-gray-300 text-sm block mb-1">
                      {t(lang, 'media_url')}
                      <span className="text-gray-500 text-xs ml-2">({t(lang, 'optional')})</span>
                    </label>
                    <div className="relative">
                      <input
                        name="media_url"
                        value={formData.media_url}
                        onChange={handleChange}
                        placeholder={t(lang, 'media_url_placeholder')}
                        className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 rounded-lg px-4 py-3 pl-10 focus:border-neon-cyan focus:outline-none"
                        disabled={!isFormOpen}
                        data-testid="media-url-input"
                      />
                      <div className="absolute left-3 top-1/2 -translate-y-1/2">
                        {formData.media_url && getMediaType(formData.media_url) === 'spotify' ? (
                          <Music className="w-5 h-5 text-green-500" />
                        ) : formData.media_url && getMediaType(formData.media_url) === 'youtube' ? (
                          <Youtube className="w-5 h-5 text-red-500" />
                        ) : (
                          <LinkIcon className="w-5 h-5 text-gray-500" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="text-gray-300 text-sm block mb-1">
                      {t(lang, 'msg')}
                      <span className="text-gray-500 text-xs ml-2">({t(lang, 'optional')})</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t(lang, 'msg_placeholder')}
                      rows={2}
                      className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 rounded-lg px-4 py-3 focus:border-neon-pink focus:outline-none resize-none"
                      disabled={!isFormOpen}
                      data-testid="message-input"
                    />
                  </div>
                </div>
              )}

              {/* Banned Word Warning Modal */}
              {showBannedWarning && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 animate-in slide-in-from-top-2" data-testid="banned-warning-modal">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">⚠️</span>
                    <div className="flex-1">
                      <p className="text-amber-300 font-semibold mb-1">{t(lang, 'banned_warning_title')}</p>
                      <p className="text-gray-300 text-sm mb-3">
                        {t(lang, 'banned_warning_text')}: <span className="text-neon-pink font-semibold">{currentEvent?.authority_label || 'le donneur d\'ordre'}</span>
                      </p>
                      <p className="text-gray-400 text-xs mb-3">
                        Mot détecté : <span className="text-amber-400">"{matchedBannedWord}"</span>
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setShowBannedWarning(false)}
                          className="flex-1 bg-white/10 text-white rounded-lg py-2 text-sm hover:bg-white/20"
                        >
                          {t(lang, 'banned_warning_modify')}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleSubmit(e, true)}
                          className="flex-1 bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-lg py-2 text-sm hover:bg-amber-500/30"
                          data-testid="force-submit-btn"
                        >
                          {t(lang, 'banned_warning_send')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isFormOpen || submitting || isPlayedDuplicate || exactDuplicate}
                className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] rounded-full py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-request-btn"
              >
                {submitting ? t(lang, 'sending') : t(lang, 'send')}
              </button>

              {/* See Other Requests Button */}
              <button
                type="button"
                onClick={goToList}
                className="w-full bg-white/10 text-white font-semibold rounded-full py-3 hover:bg-white/20 transition-colors"
              >
                <List className="w-5 h-5 inline mr-2" />
                {t(lang, 'other')}
              </button>
            </form>

            {/* Pro Mode: Soutenir le DJ Card - ALWAYS LAST */}
            {showProExtras && (djInfo?.paypal_username || djInfo?.review_url) && (
              <div className="bg-white/5 border border-neon-pink/30 rounded-xl p-4 mt-6">
                <p className="text-neon-pink font-semibold mb-2">{t(lang, 'don_title')}</p>
                <p className="text-gray-400 text-sm mb-3">{t(lang, 'don_text')}</p>
                
                <div className="space-y-2">
                  {/* PayPal Tip Button */}
                  {djInfo?.paypal_username && (
                    <a
                      href={`https://paypal.me/${djInfo.paypal_username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-[#0070BA]/20 border border-[#0070BA] text-white rounded-lg py-2.5 text-center hover:bg-[#0070BA]/30 transition-colors"
                    >
                      <span>💰</span>
                      {t(lang, 'don_paypal')}
                    </a>
                  )}
                  
                  {/* Review Button */}
                  {djInfo?.review_url && (
                    <a
                      href={djInfo.review_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-amber-500/20 border border-amber-500 text-white rounded-lg py-2.5 text-center hover:bg-amber-500/30 transition-colors"
                    >
                      <span>⭐</span>
                      {t(lang, 'don_review')}
                    </a>
                  )}
                </div>
                
                <p className="text-gray-500 text-xs text-center mt-3">{t(lang, 'don_tip_msg')}</p>
              </div>
            )}
          </>
        )}

        {/* ========== VIEW: LIST ========== */}
        {view === 'list' && (
          <div ref={listRef}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl">{t(lang, 'all')}</h2>
              <button
                onClick={goToForm}
                className="text-neon-cyan text-sm font-semibold hover:underline"
              >
                + {t(lang, 'make_request')}
              </button>
            </div>

            {/* Phase 6: Event Name in List View */}
            {currentEvent && (currentEvent.status === 'draft' || currentEvent.status === 'active') && (
              <div className="bg-gradient-to-r from-neon-pink/10 to-neon-cyan/10 border border-neon-pink/20 rounded-xl p-3 mb-4 text-center">
                <p className="text-white font-medium">
                  {t(lang, 'event_party')} : "{currentEvent.name}"
                </p>
              </div>
            )}

            {/* No active event message - ONLY for PRO DJs */}
            {djIsPro && !activeEvent && requests.length === 0 ? (
              <div className="text-center py-12">
                <Music className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 font-semibold">{t(lang, 'no_event')}</p>
                <p className="text-gray-500 text-sm mt-2">{t(lang, 'no_event_text')}</p>
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12">
                <Music className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">{t(lang, 'empty')}</p>
                <button
                  onClick={goToForm}
                  className="mt-4 text-neon-cyan hover:underline"
                >
                  {t(lang, 'be_first')}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Phase 2: Top 3 Most Supported */}
                {(() => {
                  const top3 = [...requests]
                    .filter(r => (r.likes || 0) > 0)
                    .sort((a, b) => (b.likes || 0) - (a.likes || 0))
                    .slice(0, 3);
                  
                  if (top3.length > 0) {
                    return (
                      <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-4 mb-4">
                        <h3 className="text-orange-400 font-semibold mb-3 flex items-center gap-2">
                          <Flame className="w-5 h-5" />
                          {t(lang, 'top_supported')}
                        </h3>
                        <div className="space-y-2">
                          {top3.map((req, idx) => (
                            <div key={req.id} className="flex items-center gap-3">
                              <span className="text-2xl">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-white font-medium truncate">{req.song_title}</p>
                                {req.artist && <p className="text-gray-400 text-xs">{req.artist}</p>}
                              </div>
                              <span className="text-orange-400 text-sm font-semibold">
                                🔥 {req.likes}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* All Requests - Sorted by likes desc, then by date asc */}
                {[...requests]
                  .sort((a, b) => {
                    const likeDiff = (b.likes || 0) - (a.likes || 0);
                    if (likeDiff !== 0) return likeDiff;
                    return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                  })
                  .map((req) => {
                  const status = getStatusLabel(req.status);
                  const isLiked = (req.liked_by || []).includes(getClientId());
                  const mediaType = getMediaType(req.media_url);
                  const likes = req.likes || 0;
                  
                  return (
                    <div
                      key={req.id}
                      className={`bg-white/5 rounded-xl p-4 border ${
                        req.status === 'refused' ? 'border-red-500/30 opacity-70' : 'border-white/10'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-white truncate">{req.song_title}</p>
                            {/* Media Link Icon */}
                            {req.media_url && (
                              <a 
                                href={req.media_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex-shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {mediaType === 'spotify' ? (
                                  <Music className="w-4 h-4 text-green-500 hover:text-green-400" />
                                ) : mediaType === 'youtube' ? (
                                  <Youtube className="w-4 h-4 text-red-500 hover:text-red-400" />
                                ) : (
                                  <LinkIcon className="w-4 h-4 text-gray-400 hover:text-white" />
                                )}
                              </a>
                            )}
                          </div>
                          {req.artist && <p className="text-gray-400 text-sm">{req.artist}</p>}
                          <p className="text-neon-cyan text-xs mt-1">— {req.client_name}</p>
                          {req.message && <p className="text-gray-500 text-xs mt-1 italic">"{req.message}"</p>}
                          
                          {/* Phase 2: Enhanced likes display */}
                          {likes > 0 && (
                            <p className="text-orange-400 text-xs mt-2 flex items-center gap-1">
                              <Flame className="w-3 h-3" />
                              {likes} {likes === 1 ? t(lang, 'person_supports') : t(lang, 'people_support')}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`${status.color} text-white text-xs px-2 py-1 rounded-full`}>
                            {status.text}
                          </span>
                          {/* Refused reason - displayed below status */}
                          {req.status === 'refused' && req.refused_reason && (
                            <span className="text-red-400 text-xs italic max-w-[150px] text-right">
                              {getRefusedReasonText(req.refused_reason)}
                            </span>
                          )}
                          <button
                            onClick={() => handleLike(req.id)}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors ${
                              isLiked 
                                ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' 
                                : 'bg-white/5 text-gray-400 hover:bg-orange-500/10 hover:text-orange-400'
                            }`}
                          >
                            <Flame className={`w-3 h-3 ${isLiked ? 'fill-current' : ''}`} />
                            {isLiked ? '🔥' : '+1'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </main>

      {/* Footer */}
      <footer className="text-center py-6">
        <div className="flex items-center justify-center gap-2">
          <img src="/assets/trackvibes-logo.png" alt="TrackVibes" className="h-6 w-auto" />
          <span className="text-xs text-gray-500">© 2026 TrackVibes</span>
        </div>
      </footer>
    </div>
  );
}
